import { useState, useEffect, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Database,
  Upload,
  Search,
  FileText,
  Clock,
  FolderTree,
  Settings,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Tag,
} from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import {
  ActionCard,
  ActionCardGrid,
  ActionCardSection,
} from "@/components/ui/action-card";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";

import { ragApi } from "@/services/api/rag";

export const Route = createFileRoute("/portal/knowledgebase-file/")({
  component: RAGDashboard,
});

interface NavCard {
  title: string;
  description: string;
  icon: typeof Database;
  path: string;
  iconColor: string;
  /** Only visible to platform admins (user.is_admin === true) */
  platformAdminOnly?: boolean;
}

const navCards: NavCard[] = [
  {
    title: "Query",
    description: "Ask questions about your documents",
    icon: Search,
    path: "/portal/knowledgebase-file/query",
    iconColor: "bg-purple-500",

  },
  {
    title: "Documents",
    description: "Browse and manage indexed documents",
    icon: FileText,
    path: "/portal/knowledgebase-file/documents",
    iconColor: "bg-blue-500",

  },
  {
    title: "Upload",
    description: "Upload new documents for processing",
    icon: Upload,
    path: "/portal/knowledgebase-file/upload",
    iconColor: "bg-emerald-500",

  },
  {
    title: "Categories",
    description: "Organize documents into categories",
    icon: FolderTree,
    path: "/portal/knowledgebase-file/categories",
    iconColor: "bg-teal-500",

  },
  {
    title: "Labels",
    description: "Tag documents with labels for filtering",
    icon: Tag,
    path: "/portal/knowledgebase-file/labels",
    iconColor: "bg-violet-500",

  },
  {
    title: "Batches",
    description: "Track document processing batches",
    icon: Clock,
    path: "/portal/knowledgebase-file/batches",
    iconColor: "bg-amber-500",

  },
  {
    title: "Templates",
    description: "Configure prompt templates",
    icon: Settings,
    path: "/portal/knowledgebase-file/templates",
    iconColor: "bg-slate-700",
    platformAdminOnly: true,
  },
];

interface RAGStats {
  total_documents: number;
  indexed_documents: number;
  processing_documents: number;
  failed_documents: number;
  active_batches: number;
}

function RAGDashboard() {
  const { token, user } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const [stats, setStats] = useState<RAGStats | null>(null);
  const [loading, setLoading] = useState(true);

  const organisationId = selectedOrganisation?.organisationId || "";
  const isPlatformAdmin = user?.is_admin === true;

  // Filter nav cards based on platform admin status
  const visibleNavCards = useMemo(
    () =>
      navCards.filter((card) => {
        if (card.platformAdminOnly && !isPlatformAdmin) return false;
        return true;
      }),
    [isPlatformAdmin],
  );

  useEffect(() => {
    if (token && organisationId) {
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, organisationId]);

  const loadStats = async () => {
    if (!token) return;
    setLoading(true);
    try {
      // Load document counts by status, batches, and conversations
      const [
        indexedResponse,
        processingResponse,
        failedResponse,
        batchesResponse,
      ] = await Promise.all([
        ragApi.listDocuments(token, {
          status: "indexed",
          limit: 1,
        }),
        ragApi.listDocuments(token, {
          status: "processing",
          limit: 1,
        }),
        ragApi.listDocuments(token, {
          status: "failed",
          limit: 1,
        }),
        ragApi.listBatches(token, {
          limit: 100,
        }),
      ]);

      const batches = batchesResponse.batches;

      setStats({
        total_documents:
          indexedResponse.total +
          processingResponse.total +
          failedResponse.total,
        indexed_documents: indexedResponse.total,
        processing_documents: processingResponse.total,
        failed_documents: failedResponse.total,
        active_batches: batches.filter(
          (b) => b.status === "pending" || b.status === "processing",
        ).length,
      });
    } catch (error) {
      console.error("Failed to load knowledgebase stats:", error);
      // Set default stats on error
      setStats({
        total_documents: 0,
        indexed_documents: 0,
        processing_documents: 0,
        failed_documents: 0,
        active_batches: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Knowledgebase File | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="Knowledgebase File"
          description="Manage your document knowledge base for AI-powered search and retrieval."
          icon={Database}
          eyebrow="AI Knowledge"
          gradient
        />

        {/* Stats Overview */}
        <ActionCardSection title="Overview">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {loading ? "..." : stats?.total_documents || 0}
                </div>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <FileText className="h-3 w-3" />
                  In knowledge base
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Indexed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {loading ? "..." : stats?.indexed_documents || 0}
                </div>
                <div className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle className="h-3 w-3" />
                  Ready for search
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Processing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {loading ? "..." : stats?.processing_documents || 0}
                </div>
                <div className="flex items-center gap-1 text-sm text-blue-600">
                  <TrendingUp className="h-3 w-3" />
                  Being indexed
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Failed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {loading ? "..." : stats?.failed_documents || 0}
                </div>
                <div className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-3 w-3" />
                  Need attention
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Status Badges */}
          {stats && (stats.failed_documents > 0 || stats.active_batches > 0) && (
            <div className="flex gap-2 mt-4">
              {stats.failed_documents > 0 && (
                <Badge variant="destructive">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {stats.failed_documents} failed document
                  {stats.failed_documents !== 1 ? "s" : ""}
                </Badge>
              )}
              {stats.active_batches > 0 && (
                <Badge variant="default">
                  <Clock className="h-3 w-3 mr-1" />
                  {stats.active_batches} active batch
                  {stats.active_batches !== 1 ? "es" : ""}
                </Badge>
              )}
            </div>
          )}
        </ActionCardSection>

        {/* Navigation Cards */}
        <ActionCardSection title="Features">
          <ActionCardGrid columns={3}>
            {visibleNavCards.map((card) => (
              <ActionCard
                key={card.path}
                title={card.title}
                description={card.description}
                icon={card.icon}
                href={card.path}
                iconColor={card.iconColor}
              />
            ))}
          </ActionCardGrid>
        </ActionCardSection>
      </div>
    </>
  );
}
